"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Dialog = void 0;
const react_1 = __importStar(require("react"));
const Button_1 = require("../Button");
const controls_1 = require("../controls");
const DesignSystem_1 = require("../DesignSystem");
const DialogDescription_1 = require("./components/DialogDescription/DialogDescription");
const DialogLoading_1 = require("./components/DialogLoading/DialogLoading");
const helpers_1 = require("../helpers");
const zIndexManager_1 = require("../helpers/zIndexManager");
const Icon_1 = require("../Icon");
const Movable_1 = require("../Movable");
const View_1 = require("../View");
const DialogActions_1 = require("./components/DialogActions/DialogActions");
const Dialog_styles_1 = require("./Dialog.styles");
const types_1 = require("./types");
exports.Dialog = (0, helpers_1.createUIElement)('Dialog', (_a, ref) => {
    var { className, id: dialogId, header, title, description, actions, actionFooter, secondaryActions, onRequestClose, size = types_1.DIALOG_SIZE.m, hasSpacing = true, autoHeight = false, children, bodyContentMod = View_1.VIEW_CONTENT_MOD.fill, loading = false, movable = true, pending = false, deactivate, movingOpacity = 0.4, hasTopPadding = true, canClose = true, hasDescriptionBottomBorder, onClick, onMouseDown, onMouseUp, onMouseEnter, onMouseLeave, onKeyPress, style, ['data-selector-id']: initialDataSelectorId } = _a, rest = __rest(_a, ["className", "id", "header", "title", "description", "actions", "actionFooter", "secondaryActions", "onRequestClose", "size", "hasSpacing", "autoHeight", "children", "bodyContentMod", "loading", "movable", "pending", "deactivate", "movingOpacity", "hasTopPadding", "canClose", "hasDescriptionBottomBorder", "onClick", "onMouseDown", "onMouseUp", "onMouseEnter", "onMouseLeave", "onKeyPress", "style", 'data-selector-id']);
    (0, zIndexManager_1.useZIndexManager)({
        onKeyPress: (e) => {
            var _a;
            if (canClose && e.key === 'Escape') {
                (_a = (onRequestClose || deactivate)) === null || _a === void 0 ? void 0 : _a();
            }
            onKeyPress === null || onKeyPress === void 0 ? void 0 : onKeyPress(e);
        }
    });
    const [moving, setMoving] = (0, react_1.useState)(false);
    const winRef = (0, react_1.useRef)(null);
    const dialogRef = ref || winRef;
    const { control, target: { ref: targetRef, style: targetStyle } } = (0, Movable_1.useMovable)({
        active: !!movable,
        onStart: () => {
            setMoving(true);
            document.body.setAttribute(helpers_1.CLASS_DIALOG_MOVING_ATTR_NAME, '');
        },
        onEnd: () => {
            setMoving(false);
            document.body.removeAttribute(helpers_1.CLASS_DIALOG_MOVING_ATTR_NAME);
        },
        ref: dialogRef
    });
    const movingStyle = (0, react_1.useMemo)(() => (moving ? { opacity: movingOpacity } : {}), [moving, movingOpacity]);
    const windowStyle = (0, react_1.useMemo)(() => (Object.assign(Object.assign(Object.assign({}, style), targetStyle), movingStyle)), [movingStyle, style, targetStyle]);
    // Hack for dialog blur after remove active input element
    (0, react_1.useEffect)(() => {
        let timerRef;
        const cb = () => {
            if (timerRef) {
                clearTimeout(timerRef);
            }
            timerRef = window.setTimeout(() => {
                var _a;
                if (winRef.current && document.activeElement === document.body) {
                    (_a = winRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }
            }, 60);
        };
        const element = winRef.current;
        element === null || element === void 0 ? void 0 : element.addEventListener('blur', cb, true);
        return () => {
            if (timerRef) {
                clearTimeout(timerRef);
            }
            element === null || element === void 0 ? void 0 : element.removeEventListener('blur', cb, true);
        };
    }, []);
    const dataSelectorId = (0, react_1.useMemo)(() => (dialogId ? `window-${dialogId}` : initialDataSelectorId || ''), [dialogId, initialDataSelectorId]);
    const closeControlOnClickHandler = (0, react_1.useCallback)(() => {
        if (onRequestClose) {
            onRequestClose();
            return;
        }
        if (deactivate) {
            deactivate();
        }
    }, [deactivate, onRequestClose]);
    const closeControlOnMouseDownHandler = (0, react_1.useCallback)((e) => {
        e.stopPropagation();
    }, []);
    const hasTitleOrDescription = (0, react_1.useMemo)(() => !!(title || description), [description, title]);
    const movingVisibilityStyle = (0, react_1.useMemo)(() => (moving ? { visibility: 'hidden' } : {}), [moving]);
    const { isModernUI } = (0, DesignSystem_1.useUI)();
    const closeControl = (0, react_1.useMemo)(() => isModernUI ? (react_1.default.createElement(Button_1.Button, { "data-selector-id": "window-close", ui: Button_1.BUTTON_VIEW.text, iconSize: Icon_1.ICON_SIZES.l, disabled: moving, onClick: closeControlOnClickHandler, onMouseDown: closeControlOnMouseDownHandler, iconBefore: react_1.default.createElement("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none" },
            react_1.default.createElement("path", { id: "Shape", d: "M4.08859 4.21569L4.14645 4.14645C4.32001 3.97288 4.58944 3.9536 4.78431 4.08859L4.85355 4.14645L10 9.293L15.1464 4.14645C15.32 3.97288 15.5894 3.9536 15.7843 4.08859L15.8536 4.14645C16.0271 4.32001 16.0464 4.58944 15.9114 4.78431L15.8536 4.85355L10.707 10L15.8536 15.1464C16.0271 15.32 16.0464 15.5894 15.9114 15.7843L15.8536 15.8536C15.68 16.0271 15.4106 16.0464 15.2157 15.9114L15.1464 15.8536L10 10.707L4.85355 15.8536C4.67999 16.0271 4.41056 16.0464 4.21569 15.9114L4.14645 15.8536C3.97288 15.68 3.9536 15.4106 4.08859 15.2157L4.14645 15.1464L9.293 10L4.14645 4.85355C3.97288 4.67999 3.9536 4.41056 4.08859 4.21569L4.14645 4.14645L4.08859 4.21569Z", fill: "#161616" })) })) : (react_1.default.createElement(controls_1.WindowClose, { "data-selector-id": "window-close", isInteractable: true, isEventsBlocked: moving, onClick: closeControlOnClickHandler, onMouseDown: closeControlOnMouseDownHandler })), [closeControlOnClickHandler, closeControlOnMouseDownHandler, isModernUI, moving]);
    return (react_1.default.createElement(Dialog_styles_1.StyledDialog, Object.assign({}, rest, { ref: targetRef, style: windowStyle, role: "dialog", "aria-modal": "true", "aria-label": header, size: size, autoHeight: autoHeight, "data-selector-id": dataSelectorId, tabIndex: -1, onClick: onClick, onMouseDown: onMouseDown, onMouseUp: onMouseUp, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, moving: moving, className: (0, helpers_1.cn)('vm-Dialog', className) }),
        react_1.default.createElement(Dialog_styles_1.StyledDialogHeader, { onMouseDown: control.onMouseDown, hasClose: canClose },
            react_1.default.createElement(Dialog_styles_1.StyledDialogHeaderTitle, null, header),
            canClose && closeControl),
        hasTitleOrDescription && (react_1.default.createElement(DialogDescription_1.DialogDescription, { style: movingVisibilityStyle, title: title, hasBottomBorder: hasDescriptionBottomBorder, description: description })),
        react_1.default.createElement(Dialog_styles_1.StyledDialogBody, null,
            children && (react_1.default.createElement(Dialog_styles_1.StyledDialogContent, { align: View_1.VIEW_ALIGN.fill, hasSpacing: hasSpacing, hasTopPadding: hasTopPadding, contentMod: bodyContentMod, style: movingVisibilityStyle }, children)),
            (loading || pending) && react_1.default.createElement(DialogLoading_1.DialogLoading, { loading: loading })),
        react_1.default.createElement(Dialog_styles_1.StyledDialogFooter, { style: movingVisibilityStyle }, actionFooter ? (react_1.default.cloneElement(actionFooter, { loading })) : (react_1.default.createElement(DialogActions_1.DialogActions, { secondary: secondaryActions, primary: actions === null || actions === void 0 ? void 0 : actions.filter(Boolean), loading: loading })))));
});
//# sourceMappingURL=Dialog.js.map